import { SpaceValues } from "@/lib/utils/SpaceValues";
import { MatrixListItemMatricesFragment } from "@/gql/graphql";
import { DateRange, DateRangeDualTextInput } from "@stenajs-webui/calendar";
import { Box, Row, SeparatorLine, Space } from "@stenajs-webui/core";
import {
  Card,
  CardBody,
  CardHeader,
  FlatButton,
} from "@stenajs-webui/elements";
import { cssColor } from "@stenajs-webui/theme";
import { ActionPrompt, Popover } from "@stenajs-webui/tooltip";
import { format } from "date-fns";
import * as React from "react";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MatrixFormItem } from "../types/MatrixFormModel";
import { matrixFormRedux } from "../redux/MatrixFormRedux";
import { MatrixAndKeywords } from "./MatrixAndKeywords";
import { MatrixItemValidationChips } from "./MatrixItemValidationChips";

interface Props {
  matrixItem: MatrixFormItem;
  matrix: MatrixListItemMatricesFragment;
}

export const MatrixItemForm: React.FC<Props> = ({ matrixItem, matrix }) => {
  const dispatch = useDispatch();

  const { matrices } = useSelector(matrixFormRedux.selectors.getEntity);

  const removeMatrixItem = useCallback(() => {
    dispatch(
      matrixFormRedux.actions.setEntity({
        matrices: matrices.map<MatrixFormItem>((m) =>
          m.id === matrixItem.id ? { ...m, deleted: true } : m
        ),
      })
    );
  }, [matrixItem, matrices, dispatch]);

  const changeValidity = useCallback(
    (interval: DateRange) => {
      dispatch(
        matrixFormRedux.actions.setEntity({
          matrices: matrices.map((m) =>
            m.id === matrixItem.id
              ? {
                  ...m,
                  startDate:
                    interval.startDate &&
                    format(interval.startDate, "yyyy-MM-dd"),
                  endDate:
                    interval.endDate && format(interval.endDate, "yyyy-MM-dd"),
                }
              : m
          ),
        })
      );
    },
    [dispatch, matrices, matrixItem.id]
  );

  return (
    <Card>
      <CardHeader
        text={"Validity"}
        contentAfterHeading={
          <Row alignItems={"center"}>
            <Box width={"fit-content"}>
              <DateRangeDualTextInput
                value={{
                  startDate: matrixItem.startDate
                    ? new Date(matrixItem.startDate)
                    : undefined,
                  endDate: matrixItem.endDate
                    ? new Date(matrixItem.endDate)
                    : undefined,
                }}
                onValueChange={(v) => changeValidity(v)}
              />
            </Box>
            <Space num={SpaceValues.THIRTYTWO} />
            <MatrixItemValidationChips matrixItem={matrixItem} />
          </Row>
        }
        contentRight={
          <Popover
            placement={"left"}
            trigger={"click"}
            renderTrigger={(props) => (
              <FlatButton label={"Remove"} {...props} />
            )}
          >
            {({ onRequestClose }) => (
              <ActionPrompt
                onNo={onRequestClose}
                onYes={() => {
                  removeMatrixItem();
                  onRequestClose();
                }}
              />
            )}
          </Popover>
        }
      />
      <SeparatorLine color={cssColor("--lhds-color-ui-300")} />
      <CardBody>
        <MatrixAndKeywords matrix={matrix} />
      </CardBody>
    </Card>
  );
};
